import React from 'react';
import { Table, Button, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createUseStyles } from 'react-jss';
import type { SimulationDetail, SimulationShipmentBatch } from '../../store/simulation/types';
import type { ShipiumTagConfig } from '../../components/shared/ShipiumTag';
import { ShipiumTagWithConfig } from '../../components/shared/ShipiumTag';
import { useStoreActions, useStoreState } from '../../store';
import { format } from 'date-fns/format';
import { useSearchParams } from 'react-router-dom-v5-compat';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    // disable hover
    '& .ant-table-tbody > tr.ant-table-row:hover > td': {
      background: 'unset',
    },
  },
  buttonLink: {
    fontFamily: 'monospace',
    fontSize: '1em',
    padding: 0,
  },
  button: {
    fontFamily: 'monospace',
    fontSize: '0.9em',
    width: '100px',
  },
});

export interface SimulationShipmentBatchTableProps {
  simulation?: SimulationDetail;
}
export const SimulationShipmentBatchTable = ({ simulation }: SimulationShipmentBatchTableProps) => {
  const simulationShipmentBatches = simulation?.simulationShipmentBatches || [];
  const classes = useStyles();
  const loading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const [query, setQuery] = useSearchParams();
  const batchStatus = query.get('batch-status');
  const updateSimulationShipmentBatch = useStoreActions((actions) => actions.simulation.updateSimulationShipmentBatch);
  const confirmModal = Modal.confirm;

  const updateQueryForBatch = (batch: SimulationShipmentBatch, status: string) => {
    query.set('date', batch.year + '-' + batch.month + '-' + batch.day);
    query.set('hour', batch.hour.toString());
    query.set('status', status);
    query.set('batch', batch.simulationBatchId);
    query.set('page', '1');
    setQuery(query);
  };

  const shipiumTagMapStatus: Record<string, ShipiumTagConfig> = {
    pending: { color: 'Grey', label: 'Pending', dark: true, icon: 'wait' },
    processing: { color: 'Blue', label: 'Processing', dark: true, icon: 'processing' },
    completed: { color: 'Teal', label: 'Complete', dark: true, icon: 'check' },
    failed: { color: 'Coral', label: 'Exception', dark: true, icon: 'exclamation' },
    cancelled: { color: 'Gold', label: 'Cancelled', dark: true, icon: 'warning' },
    batch_creation_error: { color: 'Coral', label: 'Error', dark: true, icon: 'exclamation' },
  };

  const cancelBatchConfirm = (simulationId: string, batchId: string) => {
    confirmModal({
      title: `Confirm Batch Cancellation`,
      content: (
        <p>
          Are you sure you want to proceed? <br />
          <br />
          This batch will not be processed and all shipments will be removed from the simulation.
        </p>
      ),
      onOk() {
        updateSimulationShipmentBatch({
          simulationId: simulationId,
          simulationBatchId: batchId,
          batchStatus: 'cancelled',
        });
      },
    });
  };

  const columns: ColumnsType<SimulationShipmentBatch> = [
    {
      title: 'Date',
      key: 'name',
      align: 'left',
      width: 300,
      render: (val: SimulationShipmentBatch) => {
        const batchDate = new Date();
        batchDate.setFullYear(val.year);
        batchDate.setMonth(val.month - 1);
        batchDate.setDate(val.day);
        return format(batchDate, 'PPPP');
      },
    },
    {
      title: 'Hour',
      key: 'name',
      align: 'center',
      render: (val: SimulationShipmentBatch) => {
        return `${val.hour.toString().padStart(2, '0')}:00`;
      },
    },
    {
      title: 'Simulated',
      key: 'simulatedShipmentsCount',
      align: 'right',
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) => (a.successRows || 0) - (b.successRows || 0),
      render: (val: SimulationShipmentBatch) => {
        if (val.successRows && val.successRows > 0) {
          return (
            <Button className={classes.buttonLink} onClick={() => updateQueryForBatch(val, 'success')} type={'link'}>
              {val.successRows}
            </Button>
          );
        } else {
          return val.batchStatus === 'completed' ? 0 : '';
        }
      },
    },
    {
      title: 'Skipped',
      key: 'simulatedShipmentsCount',
      align: 'right',
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) => (a.skippedRows || 0) - (b.skippedRows || 0),
      render: (val: SimulationShipmentBatch) => {
        if (val.skippedRows && val.skippedRows > 0) {
          return (
            <Button className={classes.buttonLink} onClick={() => updateQueryForBatch(val, 'skipped')} type={'link'}>
              {val.skippedRows}
            </Button>
          );
        } else {
          return val.batchStatus === 'completed' ? 0 : '';
        }
      },
    },
    {
      title: 'Errors',
      key: 'simulatedShipmentsCount',
      align: 'right',
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) => (a.errorRows || 0) - (b.errorRows || 0),
      render: (val: SimulationShipmentBatch) => {
        if (val.batchStatus === 'failed') {
          return '';
        } else if (val.errorRows && val.errorRows > 0) {
          return (
            <Button className={classes.buttonLink} onClick={() => updateQueryForBatch(val, 'error')} type={'link'}>
              {val.errorRows}
            </Button>
          );
        } else {
          return val.batchStatus === 'completed' ? 0 : '';
        }
      },
    },
    {
      title: 'Total',
      key: 'simulatedShipmentsCount',
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) => (a.totalRows || 0) - (b.totalRows || 0),
      align: 'right',
      render: (val: SimulationShipmentBatch) => {
        if (val.totalRows && val.totalRows > 0 && val.batchStatus === 'completed') {
          return (
            <Button className={classes.buttonLink} onClick={() => updateQueryForBatch(val, 'all')} type={'link'}>
              {val.totalRows}
            </Button>
          );
        } else {
          return val.totalRows;
        }
      },
    },
    {
      title: 'p90 Response Time',
      key: 'status',
      width: 180,
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) =>
        (a.performanceMetrics?.p90DurationMs || 0) - (b.performanceMetrics?.p90DurationMs || 0),
      align: 'right',
      render: (val: SimulationShipmentBatch) => {
        if (val.totalRows && val.batchStatus === 'completed' && val.performanceMetrics?.p90DurationMs) {
          return <span>{val.performanceMetrics?.p90DurationMs} ms</span>;
        }
      },
    },
    {
      title: 'Processing Duration',
      key: 'status',
      sorter: (a: SimulationShipmentBatch, b: SimulationShipmentBatch) =>
        (a.performanceMetrics?.durationMs || 0) - (b.performanceMetrics?.durationMs || 0),
      width: 180,
      align: 'right',
      render: (val: SimulationShipmentBatch) => {
        if (val.totalRows && val.batchStatus === 'completed') {
          const minutes = (val.performanceMetrics?.durationMs / 1000 / 60).toFixed(1);
          return <span>{minutes} minutes</span>;
        }
      },
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (val: SimulationShipmentBatch) => {
        return (
          <ShipiumTagWithConfig style={{ width: '120px' }} configMap={shipiumTagMapStatus} tag={val.batchStatus} />
        );
      },
    },
  ];

  if (simulation?.simulationStatus === 'processing') {
    columns.push({
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (val: SimulationShipmentBatch) => {
        if (val.batchStatus === 'completed') {
          return (
            <Button
              className={classes.button}
              onClick={() => {
                updateSimulationShipmentBatch({
                  simulationId: val.simulationId,
                  simulationBatchId: val.simulationBatchId,
                  batchStatus: 'pending',
                });
              }}
              size={'small'}
              ghost={true}
            >
              Reprocess
            </Button>
          );
        } else if (val.batchStatus === 'processing' || val.batchStatus === 'pending') {
          return (
            <Button
              onClick={() => cancelBatchConfirm(val.simulationId, val.simulationBatchId)}
              className={classes.button}
              size={'small'}
              danger
              ghost={true}
            >
              Cancel
            </Button>
          );
        } else if (val.batchStatus === 'failed' || val.batchStatus === 'cancelled') {
          return (
            <Button
              onClick={() => {
                updateSimulationShipmentBatch({
                  simulationId: val.simulationId,
                  simulationBatchId: val.simulationBatchId,
                  batchStatus: 'pending',
                });
              }}
              className={classes.button}
              size={'small'}
              ghost={true}
            >
              Retry
            </Button>
          );
        }
      },
    });
  }

  const data = simulationShipmentBatches
    .sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      if (a.month !== b.month) {
        return a.month - b.month;
      }
      if (a.day !== b.day) {
        return a.day - b.day;
      }
      if (a.hour !== b.hour) {
        return a.hour - b.hour;
      }
      return 0;
    })
    .filter((batch: any) => {
      return batchStatus === null || batch.batchStatus === batchStatus;
    })
    .map((batch: any, index) => {
      batch.key = batch.simulationBatchId;
      return batch;
    });

  return (
    <Table
      loading={loading}
      size={'small'}
      columns={columns}
      dataSource={data}
      pagination={{ pageSize: 24, showSizeChanger: false }}
      bordered={false}
      className={classes.tableStyles}
    />
  );
};
